import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaFolderOpen } from '@react-icons/all-files/fa/FaFolderOpen';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField, SelectOption } from '../../../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/categoria-actividad.reducer';
import axios from 'axios';
import {
  CATEGORIA_ACTIVIDAD_BASE,
  categoriaActividadListUrl,
  getCategoriaActividadListSearch,
} from '../../../../shared/categoria-actividad';

const BACK = CATEGORIA_ACTIVIDAD_BASE;

interface CategoriaActividadForm {
  id: string;
  descripcion: string;
  tipoActividad: string;
}

const empty: CategoriaActividadForm = {
  id: '',
  descripcion: '',
  tipoActividad: '',
};

export default function CategoriaActividadFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const listSearch = getCategoriaActividadListSearch(location);
  const backToList = () => navigate({ pathname: BACK, search: listSearch });

  const entity = useAppSelector(s => s.categoriaActividad.entity) as any;
  const updating = useAppSelector(s => s.categoriaActividad.updating);
  const updateSuccess = useAppSelector(s => s.categoriaActividad.updateSuccess);
  const errorMessage = useAppSelector(s => s.categoriaActividad.errorMessage);

  const [form, setForm] = useState<CategoriaActividadForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof CategoriaActividadForm, string>>>({});
  const [tipoActividadOptions, setTipoActividadOptions] = useState<SelectOption[]>([]);

  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id: String(entity.id),
        descripcion: entity.descripcion ?? '',
        tipoActividad: entity.tipoActividad?.id != null ? String(entity.tipoActividad.id) : '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(
        { pathname: BACK, search: listSearch },
        {
          state: {
            successMsg: isNew
              ? 'Categoría de actividad creada correctamente.'
              : 'Categoría de actividad actualizada correctamente.',
          },
        },
      );
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage, showToast]);

  useEffect(() => {
    axios
      .get('api/tipo-actividad', { params: { page: 0, size: 999, sort: 'descripcion,asc' } })
      .then(res => {
        const opts: SelectOption[] = (res.data as any[]).map(t => ({
          value: String(t.id),
          label: t.descripcion ?? `#${t.id}`,
        }));
        setTipoActividadOptions(opts);
      })
      .catch(() => setTipoActividadOptions([]));
  }, []);

  const handleFieldChange = (name: keyof CategoriaActividadForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const buildPayload = () => {
    const base = {
      descripcion: form.descripcion.trim(),
      tipoActividad: { id: Number(form.tipoActividad) },
    };

    if (isNew) return base;

    return {
      ...entity,
      id: Number(form.id),
      ...base,
    };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof CategoriaActividadForm, string>> = {};
    if (!form.descripcion.trim()) errs.descripcion = 'La descripción es requerida';
    if (!form.tipoActividad) errs.tipoActividad = 'El tipo de actividad es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = buildPayload();
    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  const fields: FormField<CategoriaActividadForm>[] = useMemo(() => [
    {
      name: 'id',
      label: 'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name: 'descripcion',
      label: 'Descripción',
      required: true,
      placeholder: 'Nombre de la categoría de actividad',
    },
    {
      name: 'tipoActividad',
      label: 'Tipo actividad',
      required: true,
      type: 'select',
      placeholder: 'Seleccione tipo de actividad',
      options: tipoActividadOptions,
    },
  ], [tipoActividadOptions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Categoría de Actividad', to: categoriaActividadListUrl(listSearch), icon: <FaFolderOpen /> },
        { label: isNew ? 'Nueva Categoría' : `Editar Categoría #${id}` },
      ]} />

      <PageHeader
        icon={<FaFolderOpen />}
        iconColor="#f59e0b"
        title={isNew ? 'Nueva Categoría de Actividad' : 'Editar Categoría de Actividad'}
        subtitle={isNew
          ? 'Completa los campos para crear una nueva categoría de actividad'
          : `Modificando categoría de actividad #${id}`}
      />

      {alert && <PageAlert alert={alert} />}

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={backToList}
        accentColor="#f59e0b"
      />
    </div>
  );
}
